import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { createRoute, ErrorComponent } from '@fcom/common';
import { enumValues } from '@fcom/core/utils';
import { TourOperator } from '@fcom/common-booking/interfaces/tour-operator.interface';
import { ERROR_STEPS, MANAGE_BOOKING_STEPS } from '@fcom/common-booking/manage-booking-config';

import { checkInOrMmb } from './services/check-in-or-mmb.guard';
import { CheckInEntryModule } from './modules/check-in-entry/check-in-entry.module';
import { CheckInEntryRouteComponent } from './modules/check-in-entry/routes/check-in-entry-route.component';
import { AncillaryPurchaseCheckoutGuard } from './modules/ancillary-purchase-checkout/services/ancillary-purchase-checkout.guard';
import { AncillaryPurchaseCheckoutRouteComponent } from './modules/ancillary-purchase-checkout/routes';
import { ManageBookingEntryRouteComponent } from './modules/entry/routes/manage-booking-entry-route.component';
import { ManageBookingEntryModule } from './modules/entry/manage-booking-entry.module';
import { ManageBookingViewRouteComponent } from './modules/view/routes/view-route.component';
import { ManageBookingViewModule } from './modules/view/view.module';
import { ManageBookingReviewModule } from './modules/review/review.module';
import { ViewGuard } from './modules/view/services/view.guard';
import { PassengerDetailsGuard } from './modules/view-passengers/services/passenger-details.guard';
import { ManageBookingPassengerDetailsRouteComponent } from './modules/view-passengers/routes/passenger-details-route.component';
import { ManageBookingRoutingComponent } from './components/manage-booking-routing/manage-booking-routing.component';
import { ManageBookingViewPassengersModule } from './modules/view-passengers/view-passengers.module';
import { ManageBookingReviewRouteComponent } from './modules/review/routes/review-route.component';
import { ancillaryPurchaseReviewGuard } from './modules/review/services/ancillary-purchase-review.guard';
import { ManageBookingPurchaseSuccessRouteComponent } from './modules/purchase-success/routes/purchase-success-route.component';
import { ManageBookingPurchaseSuccessGuard } from './modules/purchase-success/services/purchase-success.guard';
import { ManageBookingPurchaseSuccessModule } from './modules/purchase-success/purchase-success.module';
import { AncillaryPurchaseCheckoutModule } from './modules/ancillary-purchase-checkout';
import { ManageBookingErrorRouteGuard } from './services/error-route.guard';
import { PassengerDetailsDeactivateGuard } from './modules/view-passengers/services/passenger-details-deactivate.guard';
import { ManageBookingTravelPreparationsRouteComponent } from './modules/travel-preparations/routes/travel-preparations-route.component';
import { TravelPreparationsGuard } from './modules/travel-preparations/services/travel-preparations.guard';
import { ManageBookingTravelPreparationsModule } from './modules/travel-preparations/travel-preparations.module';
import { CheckInFlowGuard } from './services/check-in-flow-guard';
import { CheckInBoardingPassRouteComponent } from './modules/check-in/routes/check-in-boarding-pass-route.component';
import { CheckInModule } from './modules/check-in/check-in.module';
import { CarTrawlerWidgetModule } from './modules/common/components/cartrawler-widget/cartrawler-widget.module';
import { ManageBookingCarRentalModule } from './modules/car-rental/car-rental.module';
import { ManageBookingCarRentalRouteComponent } from './modules/car-rental/routes/car-rental-route.component';
import { CarRentalGuard } from './modules/car-rental/services/car-rental.guard';
import { CheckInFunnelRoutingComponent } from './modules/check-in/routes/check-in-funnel-routing.component';
import { CheckInConfirmationRouteComponent } from './modules/check-in/routes/check-in-confirmation-route.component';
import { CheckInPassengersRouteComponent } from './modules/check-in/routes/check-in-passengers-route.component';
import { CheckInSeatmapRouteComponent } from './modules/check-in/routes/check-in-seatmap-route.component';
import { CheckInStep } from './enums';
import { CheckInKnowYourTripRouteComponent } from './modules/check-in/routes/check-in-know-your-trip-route.component';
import { CheckInRulesRouteComponent } from './modules/check-in/routes/check-in-rules-route.component';
import { CheckInApisRouteComponent } from './modules/check-in/routes/check-in-apis-route.component';
import { CheckInChildSafetyNoticeRouteComponent } from './modules/check-in/routes/check-in-child-safety-notice-route.component';

export const manageBookingRoutes: Routes = [
  {
    path: '',
    component: ManageBookingRoutingComponent,
    children: [
      createRoute(MANAGE_BOOKING_STEPS.CHECK_IN, CheckInEntryRouteComponent, [], [checkInOrMmb]),
      {
        ...createRoute(MANAGE_BOOKING_STEPS.CHECK_IN_STEPS, CheckInFunnelRoutingComponent, [], [CheckInFlowGuard]),
        children: [
          {
            path: CheckInStep.PASSENGERS,
            component: CheckInPassengersRouteComponent,
          },
          {
            path: CheckInStep.CHILD_SAFETY_NOTICE,
            component: CheckInChildSafetyNoticeRouteComponent,
          },
          {
            path: CheckInStep.APIS,
            component: CheckInApisRouteComponent,
          },
          {
            path: CheckInStep.SEATMAP,
            component: CheckInSeatmapRouteComponent,
          },
          {
            path: CheckInStep.KYT,
            component: CheckInKnowYourTripRouteComponent,
          },
          {
            path: CheckInStep.KYT + '/payment/error',
            data: {
              isPaymentError: true,
            },
            component: CheckInKnowYourTripRouteComponent,
          },
          {
            path: CheckInStep.RULES,
            component: CheckInRulesRouteComponent,
          },
          {
            path: CheckInStep.RULES + '/payment/success',
            data: {
              isPaymentSuccess: true,
            },
            component: CheckInRulesRouteComponent,
          },
          {
            path: CheckInStep.CONFIRMATION,
            component: CheckInConfirmationRouteComponent,
          },
        ],
      },
      createRoute(
        MANAGE_BOOKING_STEPS.CHECK_IN_BOARDING_PASS,
        CheckInBoardingPassRouteComponent,
        [],
        [CheckInFlowGuard],
        []
      ),
      createRoute(MANAGE_BOOKING_STEPS.ENTRY, ManageBookingEntryRouteComponent, [], [checkInOrMmb]),
      ...enumValues(TourOperator).map((operator) =>
        createRoute({ path: operator, data: { operator, server: true } }, ManageBookingEntryRouteComponent, [], [], [])
      ),
      createRoute(MANAGE_BOOKING_STEPS.VIEW, ManageBookingViewRouteComponent, [], [ViewGuard]),
      createRoute(
        MANAGE_BOOKING_STEPS.VIEW_PASSENGERS,
        ManageBookingPassengerDetailsRouteComponent,
        [PassengerDetailsDeactivateGuard],
        [PassengerDetailsGuard]
      ),
      createRoute(MANAGE_BOOKING_STEPS.CAR_RENTAL, ManageBookingCarRentalRouteComponent, [], [CarRentalGuard]),
      createRoute(
        MANAGE_BOOKING_STEPS.TRAVEL_PREPARATION,
        ManageBookingTravelPreparationsRouteComponent,
        [],
        [TravelPreparationsGuard]
      ),
      createRoute(MANAGE_BOOKING_STEPS.REVIEW, ManageBookingReviewRouteComponent, [], [ancillaryPurchaseReviewGuard]),
      createRoute(
        MANAGE_BOOKING_STEPS.CHECKOUT,
        AncillaryPurchaseCheckoutRouteComponent,
        [],
        [AncillaryPurchaseCheckoutGuard]
      ),
      createRoute(
        MANAGE_BOOKING_STEPS.CONFIRMATION,
        ManageBookingPurchaseSuccessRouteComponent,
        [],
        [ManageBookingPurchaseSuccessGuard]
      ),
      createRoute(ERROR_STEPS.PAYMENT_ERROR, ErrorComponent, [], [ManageBookingErrorRouteGuard]),
      createRoute(ERROR_STEPS.ERROR_ROUTE, ErrorComponent, [], []),
      createRoute(ERROR_STEPS.ERROR, ErrorComponent, [], []),
    ],
  },
];

@NgModule({
  imports: [
    ManageBookingEntryModule,
    ManageBookingViewModule,
    ManageBookingReviewModule,
    ManageBookingTravelPreparationsModule,
    ManageBookingViewPassengersModule,
    ManageBookingCarRentalModule,
    ManageBookingPurchaseSuccessModule,
    AncillaryPurchaseCheckoutModule,
    CarTrawlerWidgetModule,
    CheckInEntryModule,
    CheckInModule,
    RouterModule.forChild(manageBookingRoutes),
  ],
  declarations: [],
  providers: [ManageBookingErrorRouteGuard],
  exports: [RouterModule],
})
export class ManageBookingRoutesModule {}
